import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Upload, RefreshCw, ChevronLeft, ChevronRight, Loader2, Trash2, Search, Play } from 'lucide-react'
import { useDocuments, useParseDocuments, useDeleteDocuments } from '@/hooks/useDocuments'
import { useDocumentPolling } from '@/hooks/useDocumentPolling'
import { useToast } from '@/hooks/use-toast'
import { UploadDocumentsModal } from './UploadDocumentsModal'
import { DocumentActions } from './DocumentActions'
import { ChunksDialog } from './ChunksDialog'
import { formatDistanceToNow } from 'date-fns'

// Helper function to get status display info
function getStatusDisplay(status: string) {
  switch (status) {
    case 'UNSTART':
      return { label: 'pending', variant: 'secondary' as const, className: 'bg-muted text-muted-foreground' }
    case 'DONE':
      return { label: 'parsed', variant: 'default' as const, className: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800' }
    case 'RUNNING':
      return { label: 'processing', variant: 'default' as const, className: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800' }
    case 'FAIL':
      return { label: 'failed', variant: 'destructive' as const, className: '' }
    default:
      return { label: status.toLowerCase(), variant: 'outline' as const, className: '' }
  }
}

interface DocumentsTabProps {
  kbId: string
  kbStats: {
    document_count: number
    ready_document_count: number
    updated_at: string
  }
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200]

export function DocumentsTab({ kbId, kbStats }: DocumentsTabProps) {
  const { toast } = useToast()
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [chunksDialogOpen, setChunksDialogOpen] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState<{ id: string; name: string; status: string } | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set())
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  
  // Debounce search input
  useMemo(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      setCurrentPage(1) // Reset to first page on search
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])
  
  // Server-side pagination with search
  const offset = (currentPage - 1) * itemsPerPage
  const { data, isLoading, refetch } = useDocuments(kbId, itemsPerPage, offset, debouncedSearch || undefined)
  const parseDocuments = useParseDocuments(kbId)
  const deleteDocuments = useDeleteDocuments(kbId)
  
  // Reset to page 1 after upload
  const handleUploadSuccess = () => {
    setCurrentPage(1)
    setSelectedDocIds(new Set())
    refetch()
  }
  
  // Selection handlers
  const toggleDocSelection = (ragflowDocId: string) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev)
      if (next.has(ragflowDocId)) {
        next.delete(ragflowDocId)
      } else {
        next.add(ragflowDocId)
      }
      return next
    })
  }
  
  const toggleSelectAll = () => {
    const selectableDocIds = documents
      .filter(doc => doc.ragflow_document_id)
      .map(doc => doc.ragflow_document_id!)
    
    if (selectedDocIds.size === selectableDocIds.length) {
      setSelectedDocIds(new Set())
    } else {
      setSelectedDocIds(new Set(selectableDocIds))
    }
  }
  
  // Bulk actions
  const handleBulkParse = async () => {
    if (selectedDocIds.size === 0) return
    
    try {
      await parseDocuments.mutateAsync(Array.from(selectedDocIds))
      toast({
        title: 'Parsing started',
        description: `${selectedDocIds.size} document(s) are being parsed`,
      })
      setSelectedDocIds(new Set())
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to parse documents',
        variant: 'destructive',
      })
    }
  }
  
  const handleParseAllUnparsed = async () => {
    // Get all unparsed document IDs from current page
    const unparsedDocIds = documents
      .filter(doc => doc.ragflow_document_id && (doc.status === 'UNSTART' || doc.status === 'FAIL'))
      .map(doc => doc.ragflow_document_id!)
    
    if (unparsedDocIds.length === 0) {
      toast({
        title: 'No documents to parse',
        description: 'All visible documents are already parsed or processing',
      })
      return
    }
    
    try {
      await parseDocuments.mutateAsync(unparsedDocIds)
      toast({
        title: 'Parsing started',
        description: `${unparsedDocIds.length} document(s) are being parsed`,
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to parse documents',
        variant: 'destructive',
      })
    }
  }
  
  const handleBulkDelete = async () => {
    if (selectedDocIds.size === 0) return
    
    if (!confirm(`Are you sure you want to delete ${selectedDocIds.size} document(s)?`)) {
      return
    }
    
    try {
      await deleteDocuments.mutateAsync(Array.from(selectedDocIds))
      toast({
        title: 'Documents deleted',
        description: `${selectedDocIds.size} document(s) have been deleted`,
      })
      setSelectedDocIds(new Set())
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to delete documents',
        variant: 'destructive',
      })
    }
  }
  
  // Handle document click to view chunks
  const handleDocumentClick = (doc: any) => {
    if (doc.ragflow_document_id) {
      setSelectedDocument({
        id: doc.ragflow_document_id,
        name: doc.name,
        status: doc.status
      })
      setChunksDialogOpen(true)
    }
  }
  
  const documents = data?.documents || []
  const pagination = data?.pagination
  
  // Enable polling for documents in RUNNING state
  useDocumentPolling({
    kbId,
    documents,
    enabled: documents.some(doc => doc.status === 'RUNNING'),
    limit: itemsPerPage,
    offset
  })

  // Use stats from KB API (accurate totals)
  const totalDocs = kbStats.document_count
  const parsedDocs = kbStats.ready_document_count
  
  // Calculate total chunks from visible documents only
  const totalChunks = documents.reduce((sum, d) => sum + (d.chunk_count || 0), 0)
  
  // Use KB updated_at for last updated time
  const lastUpdated = kbStats.updated_at ? new Date(kbStats.updated_at) : null

  // Determine status
  const status = totalDocs === 0 ? 'empty' : parsedDocs === totalDocs ? 'ready' : 'processing'

  // Pagination info from server
  const totalPages = pagination ? Math.ceil(pagination.total / itemsPerPage) : 0
  const startIndex = pagination?.offset || 0
  const endIndex = Math.min(startIndex + itemsPerPage, pagination?.total || 0)

  return (
    <div className="space-y-6">
      {/* KB Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Knowledge Base Status</CardTitle>
            <span className={`px-2 py-1 rounded-md text-xs font-medium ${
              status === 'ready' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
              status === 'processing' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
              'bg-muted text-muted-foreground'
            }`}>
              {status}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-muted rounded-lg">
              <div className="text-xs text-muted-foreground mb-1">Total Documents</div>
              <div className="text-2xl font-bold">{totalDocs}</div>
            </div>
            
            <div className="p-4 bg-muted rounded-lg">
              <div className="text-xs text-muted-foreground mb-1">Parsed</div>
              <div className="text-2xl font-bold text-green-600">{parsedDocs}</div>
            </div>
            
            <div className="p-4 bg-muted rounded-lg">
              <div className="text-xs text-muted-foreground mb-1">Total Chunks</div>
              <div className="text-2xl font-bold">{totalChunks}</div>
            </div>
            
            <div className="p-4 bg-muted rounded-lg">
              <div className="text-xs text-muted-foreground mb-1">Last Updated</div>
              <div className="text-sm font-bold">
                {lastUpdated ? formatDistanceToNow(lastUpdated, { addSuffix: true }) : 'Never'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents Table Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center">
              <CardTitle>Documents</CardTitle>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => refetch()}>
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </Button>
                <Button size="sm" onClick={() => setUploadModalOpen(true)}>
                  <Upload className="h-4 w-4" />
                  Upload Documents
                </Button>
              </div>
            </div>
            
            {/* Search and Page Size Controls */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Show:</span>
                <Select
                  value={String(itemsPerPage)}
                  onValueChange={(value) => {
                    setItemsPerPage(Number(value))
                    setCurrentPage(1)
                  }}
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Bulk Actions */}
              {selectedDocIds.size > 0 ? (
                <div className="flex gap-2">
                  <Button 
                    variant="default" 
                    size="sm" 
                    onClick={handleBulkParse}
                    disabled={parseDocuments.isPending}
                  >
                    <Play className="h-4 w-4" />
                    Parse Selected ({selectedDocIds.size})
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleBulkDelete}
                    disabled={deleteDocuments.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete ({selectedDocIds.size})
                  </Button>
                </div>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleParseAllUnparsed}
                  disabled={parseDocuments.isPending || documents.filter(d => d.status === 'UNSTART' || d.status === 'FAIL').length === 0}
                >
                  <Play className="h-4 w-4" />
                  Parse Listed
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading documents...</div>
          ) : documents.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr className="text-left">
                    <th className="pb-3 text-sm font-medium text-muted-foreground w-12">
                      <Checkbox
                        checked={documents.filter(d => d.ragflow_document_id).length > 0 && selectedDocIds.size === documents.filter(d => d.ragflow_document_id).length}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <th className="pb-3 text-sm font-medium text-muted-foreground">Name</th>
                    <th className="pb-3 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="pb-3 text-sm font-medium text-muted-foreground">Size</th>
                    <th className="pb-3 text-sm font-medium text-muted-foreground">Chunks</th>
                    <th className="pb-3 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id} className="border-b">
                      <td className="py-3">
                        {doc.ragflow_document_id && (
                          <Checkbox
                            checked={selectedDocIds.has(doc.ragflow_document_id)}
                            onCheckedChange={() => toggleDocSelection(doc.ragflow_document_id!)}
                          />
                        )}
                      </td>
                      <td className="py-3">
                        <button
                          onClick={() => handleDocumentClick(doc)}
                          className="text-left hover:underline hover:text-primary cursor-pointer"
                          disabled={doc.status !== 'DONE' || !doc.ragflow_document_id}
                        >
                          {doc.name}
                        </button>
                      </td>
                      <td className="py-3">
                        {(() => {
                          const statusInfo = getStatusDisplay(doc.status)
                          return (
                            <Badge 
                              variant={statusInfo.variant}
                              className={`flex items-center gap-1 w-fit ${statusInfo.className}`}
                            >
                              {doc.status === 'RUNNING' && <Loader2 className="h-3 w-3 animate-spin" />}
                              {statusInfo.label}
                            </Badge>
                          )
                        })()}
                      </td>
                      <td className="py-3 text-sm text-muted-foreground">
                        {(doc.size / 1024).toFixed(1)} KB
                      </td>
                      <td className="py-3 text-sm">{doc.chunk_count || 0}</td>
                      <td className="py-3">
                        <DocumentActions
                          kbId={kbId}
                          ragflowDocumentId={doc.ragflow_document_id}
                          documentName={doc.name}
                          status={doc.status}
                          metadata={doc.meta_fields || {}}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {pagination && pagination.total > itemsPerPage && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {startIndex + 1} to {endIndex} of {pagination.total} documents
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={!pagination.has_previous || isLoading}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={!pagination.has_next || isLoading}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No documents yet</h3>
              <p className="text-muted-foreground mb-4">
                Upload your first document to get started
              </p>
              <Button onClick={() => setUploadModalOpen(true)}>
                <Upload className="h-4 w-4" />
                Upload Documents
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Modal */}
      <UploadDocumentsModal
        kbId={kbId}
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        onSuccess={handleUploadSuccess}
      />
      
      {/* Chunks Dialog */}
      {selectedDocument && (
        <ChunksDialog
          open={chunksDialogOpen}
          onOpenChange={setChunksDialogOpen}
          kbId={kbId}
          documentId={selectedDocument.id}
          documentName={selectedDocument.name}
          documentStatus={selectedDocument.status}
        />
      )}
    </div>
  )
}
